import React, { useContext, useEffect } from 'react';
import { Box, Button, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import BodyText from './BodyText';
import CardModelCode from '../UniCard/shared/CardModelCode';
import CardStatusBadge from '../UniCard/shared/CardStatusBadge';
import CardSpecialOfferBadge from '../UniCard/shared/CardSpecialOfferBadge';
import CompareToggleButton from './CompareToggleButton';
import Link from './Link';
import LocalStorageContext from '../LocalStorageContext/LocalStorageContext';
import PriceComponent from '../PriceComponent';
import SaveLabel from '../PriceComponent/SaveLabel';
import Thumbnail from './Thumbnail';
import Title from './Title';
import componentProps from '../MarineProductDetailsCard/componentProps';
import gtmProductCardClick from '../../core/gtm/gtmProductCardClick';
import themeStyles from './ProductCard.styles';
import { EqualHeightContext } from '../../utils/components/EqualHeight/context';
import { EqualHeightElement } from '../../utils/components/EqualHeight';
import { VIEW_DETAILS, useConfiguration } from './utils';
import { wrapJSSFields } from '../../utils/wrapJSSFields';
import usePriceFlagsForProvince from '../../utils/hooks/usePriceFlagsForProvince';

const Wrapper = themeStyles.apply(Box, 'Wrapper');
const SecondaryButton = themeStyles.apply(Button, 'SecondaryButton');
const CashSavingsContainer = themeStyles.apply(Box, 'CashSavings');

const ProductCard = ({
  fields,
  gtmTags,
  hasError,
  inventoryStatus,
  isFetching,
  prices,
  showModelCode,
  shouldSendECommerceGtmEvent,
  variant,
  equalPrefix = 'ProductCard',
  onBuyNowClick,
  itemListIndex,
  ...styledProps
}) => {
  const { t } = useTranslation();

  const { setRecentlyViewedProducts } = useContext(LocalStorageContext);
  const { setForceUpdate } = useContext(EqualHeightContext);
  const { showSellingPrice } = usePriceFlagsForProvince();

  const {
    imageOrder,
    showPricing,
    bodyMarginTop,
    bodySize,
    wrapperMaxWidth,
    ctaButtonDisplay,
    priceComponentStyles,
    compareLabelFontSize,
    compareLabelLineHeight,
    showBuyNowCta: productLineHasBuyNowCta,
    secondaryButtonMarginTop,
    hasEqualHeightInherit,
    hasDealerMessage,
    hasSellingLabel,
    thumbnailStyles,
    buttonStyling,
    hasCategories,
    showRebateDisclaimerIndicator,
  } = useConfiguration(variant);

  useEffect(() => {
    setForceUpdate(value => !value);
  }, [inventoryStatus]);

  const showBuyNowCta = fields?.disableBuyNow?.value !== '1';

  const { detIdentifier, tagline, thumbnail, modelPage, id, primaryImage, detailsPath, modelName, hasSpecialOffer } =
    wrapJSSFields(fields);
  const modelPageUrl = modelPage?.field?.fields?.url || detailsPath?.field;

  const image = primaryImage
    ? {
        src: primaryImage.field?.item?.value?.src,
        alt: primaryImage.field?.item?.value?.alt,
      }
    : {
        src: thumbnail?.value?.images?.[0]?.url,
        alt: thumbnail?.value?.images?.[0]?.alt?.value,
      };
  const productId = id?.field;
  const detId = detIdentifier?.value;
  const categoryName = modelName?.field;

  let hasDiscount;
  let discountValue;

  const saveStyles = {
    copy: {
      styles: {
        fontSize: ['14px', '14px', '14px'],
      },
    },
  };

  if (prices) {
    discountValue = prices?.discount?.priceDiscountAmount;
    hasDiscount = !!discountValue;
  }

  const handleLinkOnClick = () => {
    setRecentlyViewedProducts(detId);
    if (shouldSendECommerceGtmEvent) {
      gtmProductCardClick({
        productCode: fields?.modelCode?.value,
        productName: fields?.gtmTrimName,
        discountAmount: discountValue,
        category: fields?.gtmCategoryName,
        category2: fields?.gtmSubCategoryName,
        price: showSellingPrice ? prices?.allInPrice?.value : prices?.msrpPrice?.value,
        index: itemListIndex,
      });
    }
  };

  const wrapperGtmTags = gtmTags?.productTile;

  return (
    <Wrapper maxWidth={wrapperMaxWidth} {...styledProps} data-testid="product-card" {...wrapperGtmTags}>
      <EqualHeightElement placeholder={!hasDiscount} name={`${equalPrefix}-ProductCard-SaveLabel`}>
        <Optional when={hasDiscount}>
          <CashSavingsContainer>
            <SaveLabel
              value={discountValue}
              saveStyles={saveStyles}
              showRebateDisclaimerIndicator={showRebateDisclaimerIndicator}
            />
          </CashSavingsContainer>
        </Optional>
      </EqualHeightElement>

      <EqualHeightElement name={`${equalPrefix}-ProductCard-Title`}>
        <Link href={modelPageUrl} onClick={handleLinkOnClick} gtmTags={gtmTags?.link}>
          <Title mt="0" title={fields?.name} />
        </Link>
      </EqualHeightElement>

      <EqualHeightElement placeholder={!showModelCode} name="product-card-model-code">
        <Optional when={showModelCode}>
          <Box mt="s" mb="xxs">
            <CardModelCode modelCode={fields?.modelCode?.value} />
          </Box>
        </Optional>
      </EqualHeightElement>

      <EqualHeightElement
        placeholder={!showPricing}
        name={`${equalPrefix}-ProductCard-Price`}
        inherit={hasEqualHeightInherit}
      >
        <Optional when={showPricing}>
          <PriceComponent
            prices={prices}
            priceComponentStyles={priceComponentStyles}
            isFetching={isFetching}
            hasError={hasError}
            errorMessage={hasDealerMessage && t('Pages.Models.Exploration.contactLocalDealerLabel')}
            allInPriceTooltipLabel={hasSellingLabel && t('Shared.Common.sellingPriceTooltipText')}
          />
        </Optional>
      </EqualHeightElement>

      <EqualHeightElement placeholder={inventoryStatus === 'inStock'} name={`${equalPrefix}-ProductCard-StatusBadge`}>
        <Optional when={inventoryStatus}>
          <Box pt="xs" pb="xs">
            <CardStatusBadge status={inventoryStatus} badgeAlignment="center" />
          </Box>
        </Optional>
      </EqualHeightElement>

      <EqualHeightElement placeholder={!hasSpecialOffer?.value} name="product-card-special-offer-badge">
        <Optional when={hasSpecialOffer?.value}>
          <CardSpecialOfferBadge />
        </Optional>
      </EqualHeightElement>

      <Link href={modelPageUrl} onClick={handleLinkOnClick} gtmTags={gtmTags?.link}>
        <Thumbnail order={imageOrder} image={image} {...thumbnailStyles} />
      </Link>

      <Optional when={tagline?.value}>
        <BodyText text={tagline?.value} mt={bodyMarginTop} size={bodySize} />
      </Optional>

      <Optional when={productLineHasBuyNowCta}>
        <EqualHeightElement placeholder={!showBuyNowCta} name={`${equalPrefix}-ProductCard-BuyNow`}>
          <Optional when={showBuyNowCta}>
            <Button
              onClick={onBuyNowClick}
              styling="special"
              data-testid="product-card-buy-now"
              style={{ width: '100%' }}
              {...gtmTags?.cta}
              data-gtm-interaction-type="cta: buy now"
            >
              {t('Shared.Common.buyNowButton')}
            </Button>
          </Optional>
        </EqualHeightElement>
      </Optional>

      <SecondaryButton
        tabIndex={0}
        as="a"
        display={ctaButtonDisplay}
        mt={secondaryButtonMarginTop}
        href={modelPageUrl}
        styling={buttonStyling}
        onClick={() => handleLinkOnClick(VIEW_DETAILS)}
        {...gtmTags?.cta}
      >
        {t('Shared.Common.viewDetailsButton')}
      </SecondaryButton>
      <CompareToggleButton
        compareLabelFontSize={compareLabelFontSize}
        compareLabelLineHeight={compareLabelLineHeight}
        tabIndex={0}
        id={productId}
        ariaLabel={t('Shared.Common.compareCheckboxAria', { modelName: fields?.name })}
        gtmTags={gtmTags?.compare}
        detId={detId}
        categoryName={hasCategories && categoryName}
      />
    </Wrapper>
  );
};

ProductCard.propTypes = componentProps;

export default ProductCard;
